import axios from 'axios';
import UserService from './UserService'

  const instance = axios.create();

  instance.interceptors.request.use(
    config => {
      // On public registration pages, don't attach a stale JWT from a previous
      // session — it just causes backend 401s and noisy auth-failure logs on a
      // page where the user is intentionally unauthenticated. Match exact or
      // path+"/" so "/register" doesn't swallow "/registercentre".
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const PUBLIC_UNAUTHED_PATHS = ['/register', '/parent-register', '/alumni-register'];
      const isPublicUnauthedPath = PUBLIC_UNAUTHED_PATHS.some(
        p => path === p || path.startsWith(p + '/')
      );

      const token = UserService.getToken();
      if (token && !isPublicUnauthedPath) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    error => Promise.reject(error)
  );

  // Response interceptor to handle auth errors gracefully
  instance.interceptors.response.use(
    response => response,
    error => {
      const url = error.config?.url || '';
      const status = error.response?.status;

      // Suppress all 401/403 errors — these are session/auth issues, not app bugs.
      // Individual components handle missing data gracefully via their own state.
      // Exception: binary downloads (responseType: 'blob') must NOT receive a
      // fake {} body — the caller would create a garbage blob and the failure
      // would be invisible. Let those reject normally so the caller can surface
      // the auth problem.
      if ((status === 401 || status === 403) && error.config?.responseType !== 'blob') {
        console.warn('[axios] Auth error suppressed:', status, url);

        // 401 (expired/invalid token) additionally surfaces the session-expired modal.
        // 403 (insufficient permission) does NOT fire the modal — user is still authenticated.
        // Login endpoint is skipped so wrong-password 401s don't trigger it.
        // Fires whenever a token WAS present — including on public pre-login
        // pages (atlas, products, …): a 401 with a token means a real session
        // expired mid-use, and silently-empty content is worse than a prompt.
        // Visitors with no token never had a session, so they're never nagged.
        // Presentation is per-route inside the modal (skipped on auth-flow
        // pages, dismissible on public pages — see session-expired-modal).
        const isLoginUrl = url.includes('/authenticate/user') || url.includes('/login');
        const hadSession = !!UserService.getToken();
        if (status === 401 && !isLoginUrl && hadSession) {
          window.dispatchEvent(new CustomEvent('ilearn:session-expired'));
        }

        return Promise.resolve({
          data: {},
          status: 200,
          statusText: 'OK (suppressed auth error)',
          headers: {},
          config: error.config
        });
      }

      // For other routes, still reject so .catch() handlers can process it
      return Promise.reject(error);
    }
  );

  export default instance;